import React from 'react';
import { css } from '@emotion/css';
import SecondaryLoader from '../../../components/general/SecondaryLoader';
import TableCell from '../../../components/general/TableCell';
import { color_colors_ocean } from '../../../constants/colors';
import moment from 'moment';
import TopContentMissModel from '../../../models/analytics/api/TopContentMissModel';
import FeatureContainer from '../../../state/containers/FeatureContainer';
import ReportTable from '../../../components/analytics/ReportTable';
import ReportTableHeader from '../../../components/analytics/ReportTableHeader';
import MissStatisticsModel from '../../../models/analytics/MissStatisticsModel';
import { getDefaultFeatureTypeName, getSmallFeatureTypeIcon } from '../../../models/extensions';
import { DATE_FORMAT } from '../../../models/analytics/AnalyticsDateFormat';
import TotalCard from '../../../components/charts/TotalCard';
import DOMPurify from 'dompurify';

const fallbackIcon = require('../../../content/images/fallback-black.svg');

interface ApplicationTopMissesTableProps {
    data: TopContentMissModel
    totals: MissStatisticsModel
    previousTotals: MissStatisticsModel
    featureContainer: FeatureContainer
    isLoading: boolean
    startDate: string
    endDate: string
    showValue: boolean
    showPercentage: boolean
}

interface MissRowItem {
    title: string,
    featureTypeId: string
    slots: any,
    count: number
}


const ApplicationTopMissesTable: React.FC<ApplicationTopMissesTableProps> = (props) => {
    const timeframeLabel = `${moment(props.startDate, DATE_FORMAT).format("MMM DD, YYYY")} - ${moment(props.endDate, DATE_FORMAT).format("MMM DD, YYYY")}`;
    const contentMisses = props.totals?.data?.contentMissCount ?? 0;
    const featureMisses = props.totals?.data?.featureMissCount ?? 0;
    const previousContentMisses = props.previousTotals?.data?.contentMissCount ?? 0;
    const previousFeatureMisses = props.previousTotals?.data?.featureMissCount ?? 0;
    const deltaContentMisses = (contentMisses ?? 0) - (previousContentMisses ?? 0)
    const deltaFeatureMisses = (featureMisses ?? 0) - (previousFeatureMisses ?? 0)


    const getExportableData = () => {
        const rows = [['Interaction type', 'Subject', 'Occurrences']];
        rows.push(['System Fallback', 'none', props.totals?.data?.featureMissCount?.toString() ?? '0'])
        props.data.items.forEach(item => {
            rows.push([getDefaultFeatureTypeName(item.featureTypeId) ?? "Unknown", item.slots ? '"' + JSON.stringify(item.slots)?.replace(/\"/g, '""') + '"' : 'Unknown', item.count.toString()]);
        })
        return rows;
    }
    const getRowItems = () => {
        var items = [] as MissRowItem[]
        // only show items where the feature type is a guid to avoid showing system errors
        // this is basically here because alex is dumb and we left a typo in the backend that sets the featureTypeId to 'unkown' or 'unknown'. So guid check is cleaner..
        items.push(...props.data.items?.filter(c => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(c.featureTypeId))?.map(c => ({
            title: getDefaultFeatureTypeName(c.featureTypeId),
            featureTypeId: c.featureTypeId,
            count: c.count,
            slots: c.slots
        })) ?? [])
        return items;
    }
    const columns = [
        {
            id: "title",
            Header: <ReportTableHeader title="Interaction Type" />,
            accessor: (item: MissRowItem) => item,
            Cell: row => (
                <TableCell>
                    <img className="feature-type-icon" src={getSmallFeatureTypeIcon(row.value.featureTypeId) ? getSmallFeatureTypeIcon(row.value.featureTypeId) : fallbackIcon} />
                    <p>{row.value.title ?? "Unknown"}</p>
                </TableCell>
            )
        },
        {
            id: "slots",
            Header: <ReportTableHeader title="Subject" />,
            accessor: (item: MissRowItem) => {
                var value = "";

                Object.keys(item.slots).forEach(p => {
                    value += `<i>${p}</i> :  ${item.slots[p]} <br />`;
                })

                return value;
            },
            Cell: row => (
                <TableCell>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.value ? row.value : "<i>No slots returned</i>") }} />
                </TableCell>
            )
        },
        {
            id: "count",
            Header: <ReportTableHeader title='Occurences' />,
            accessor: (item: MissRowItem) => item.count,
            Cell: row => (
                <TableCell>
                    {row.value}
                </TableCell>
            )
        },
    ]
    return (
        <div className={containerStyle}>

            {props.isLoading || !props.data ? <div className="loader-container"><SecondaryLoader /></div> :
                <>
                    <h3>App Summary</h3>
                    <div className="totals-container">
                        <TotalCard title="Missed Intents"
                            value={featureMisses}
                            deltaValue={deltaFeatureMisses}
                            deltaPercentage={Math.round(deltaFeatureMisses / (featureMisses > 0 ? featureMisses : 1) * 100)}
                            showValue={props.showValue}
                            showPercentage={props.showPercentage} />
                        <TotalCard title="Content Misses"
                            value={contentMisses}
                            deltaValue={deltaContentMisses}
                            deltaPercentage={Math.round(deltaContentMisses / (contentMisses > 0 ? contentMisses : 1) * 100)}
                            showValue={props.showValue}
                            showPercentage={props.showPercentage} />

                    </div>
                    <h3>By Content</h3>
                    <ReportTable
                        columns={columns}
                        data={getRowItems()}
                        showPagination={false}
                        exportName={`${props.startDate}-${props.endDate}_Misses`}
                        getExportableData={getExportableData}
                    />
                </>}
        </div>
    );
}

const containerStyle = css`
.feature-type-icon {
    margin-right: 12px;
    width: 24px;
    height: 24px;
}
.loader-container {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    width: 100%;
    .spinner > div {
        background: ${color_colors_ocean};
    }
}
.totals-container{
    display: flex;
    flex: 1;
    margin: 0 16px;
    padding-bottom: 16px;
}
>h3 {
    margin: 16px 32px;
}
`

export default ApplicationTopMissesTable;